import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryService } from '@services/categoryService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'categories';

/**
 * Hook para gerenciar categorias
 */
export const useCategories = (type = null) => {
  const queryClient = useQueryClient();

  // Listar categorias
  const {
    data: categories = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY, type],
    queryFn: () => categoryService.getAll(type),
  });

  // Criar categoria
  const createMutation = useMutation({
    mutationFn: categoryService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Categoria criada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar categoria');
    },
  });

  // Atualizar categoria
  const updateMutation = useMutation({
    mutationFn: ({ categoryId, data }) => categoryService.update(categoryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Categoria atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar categoria');
    },
  });

  // Deletar categoria
  const deleteMutation = useMutation({
    mutationFn: categoryService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Categoria excluída com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir categoria');
    },
  });

  // Criar categorias padrão
  const seedMutation = useMutation({
    mutationFn: categoryService.seedDefaults,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Categorias padrão criadas!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar categorias padrão');
    },
  });

  // Separar por tipo e ordenar por nome
  const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR');
  const incomeCategories = categories.filter(c => c.type === 'income').sort(sortByName);
  const expenseCategories = categories.filter(c => c.type === 'expense').sort(sortByName);

  return {
    categories,
    incomeCategories,
    expenseCategories,
    isLoading,
    error,
    refetch,
    createCategory: createMutation.mutateAsync,
    updateCategory: updateMutation.mutateAsync,
    deleteCategory: deleteMutation.mutateAsync,
    seedDefaults: seedMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export default useCategories;
